import { useState, useEffect, useCallback, useMemo } from 'react';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { useNavigate } from '../routing';
import { documentsApi, suppliersApi, formatCurrency, statusBadge, downloadDocFile } from '../utils/api';
import { toast, updateToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateShort, formatUploadedShort, formatUploadedFull } from '../utils/dateFormat';
import DocumentsFilterBar from '../components/DocumentsFilterBar';
import ListRow from '../components/ListRow';
import MobileSort from '../components/MobileSort';
import { useIsMobile } from '../utils/useIsMobile';
import { useSortable } from '../utils/useSortable';
import { currentYearRange, periodLabel, PRESETS_DOCUMENTS } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import Icon from '../components/Icon';
import { HqOnlyLock } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';
import { DocPriceTags } from '../components/PriceSourceBadge';
import DeleteDocumentDialog from '../components/DeleteDocumentDialog';
import LinkedNoteMark from '../components/LinkedNoteMark';

// All document types the system tracks. The first six belong to suppliers (and
// also appear on the per-supplier page); קבלה / כרטסת / אחר live only here.
// Each note type is followed by its "- לא מקושרת" option — a filter key, not a
// type (utils/docTypes.js UNLINKED_NOTE_FILTER_LABELS), for every role.
const ALL_DOC_TYPES = [
  'חשבונית', 'חשבונית זיכוי',
  'תעודת משלוח', 'unlinked:תעודת משלוח',
  'תעודת החזרה', 'unlinked:תעודת החזרה',
  'חשבונית/קבלה', 'חשבון עסקה', 'קבלה', 'כרטסת', 'אחר',
];

const isCreditDoc = (t) => t === 'חשבונית זיכוי' || t === 'תעודת החזרה';


export default function DocumentsPage() {
  const navigate       = useNavigate();
  const { isAdmin }       = useAuth();
  const [docs,         setDocs]         = useState([]);
  const [suppliers,    setSuppliers]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  // Multi-select state: arrays of values. Empty array = "all" (no filter).
  // Backend accepts comma-separated lists (see buildAllDocsFilter on the
  // server) — single-value clients still work for backward compatibility.
  const [supplierIds,  setSupplierIds]  = useState([]);
  const [docTypes,     setDocTypes]     = useState([]);
  const [docNumFilter, setDocNumFilter] = useState('');
  // Admin-only "מחירים אוטומטיים" review filter (components/PriceSourceBadge.jsx).
  const [priceSources, setPriceSources] = useState([]);
  // Opens on the whole current year. This page is a LIST — a year is simply a
  // longer list, and the archive question people come here with ("where is that
  // invoice from March?") is answered by the default rather than by a second
  // trip to the date picker. It is also the one screen that offers שנה נוכחית
  // as a preset (PRESETS_DOCUMENTS), so "Clear all" returns to a period the
  // rail can highlight and the user can re-select.
  //
  // Deliberately NOT copied to /suppliers: a range longer than one calendar
  // month is approved month by month there (the approve-for-payment workflow)
  // (server/lib/monthKey.js). Computed once on mount so it stays stable across
  // renders.
  const defaultRange = useMemo(() => currentYearRange(), []);
  const [dateFrom,     setDateFrom]     = useState(defaultRange.from);
  const [dateTo,       setDateTo]       = useState(defaultRange.to);
  // Rolls a preset forward when the tab comes back after the day changed
  // (usePresetPeriod).
  usePresetPeriod(dateFrom, dateTo, PRESETS_DOCUMENTS, (f, t) => { setDateFrom(f); setDateTo(t); });
  const [openMenu,     setOpenMenu]     = useState(null);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  // 640 = the content-density breakpoint (tables/stats), not the 720 filter one.
  const isMobile = useIsMobile(640);

  const params = useMemo(() => {
    const p = {};
    if (supplierIds.length > 0) p.supplier_id = supplierIds.join(',');
    if (docTypes.length    > 0) p.doc_type    = docTypes.join(',');
    if (dateFrom)               p.date_from   = dateFrom;
    if (dateTo)                 p.date_to     = dateTo;
    if (docNumFilter.trim())    p.doc_num     = docNumFilter.trim();
    if (isAdmin && priceSources.length > 0) p.price_source = priceSources.join(',');
    return p;
  }, [supplierIds, docTypes, dateFrom, dateTo, docNumFilter, priceSources, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const r = await documentsApi.getAll(params);
      setDocs(r.data);
    } catch {
      // Was a toast only: it fires on mount, vanishes, and leaves the
      // empty state claiming there are no documents. Keep the surface.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Supplier dropdown: load every supplier — including ones whose only docs
  // are קבלה/כרטסת/אחר (the strict purchase-only filter on /api/suppliers
  // would hide them, but a user filtering this tab by such a supplier is
  // exactly the case we need to support).
  useEffect(() => {
    suppliersApi.getAll({ include_empty: true }).then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  const clearFilters = () => {
    setSupplierIds([]);
    setDocTypes([]);
    setDocNumFilter('');
    setPriceSources([]);
    // Fresh, not the mount-time defaultRange — the page may have been open
    // since last year.
    const fresh = currentYearRange();
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
  };
  const hasFilters = supplierIds.length > 0 || docTypes.length > 0 || docNumFilter || dateFrom || dateTo || (isAdmin && priceSources.length > 0);

  // The API should always return an array, but if a request fails or returns
  // an unexpected shape, guard so we don't crash on .reduce / .map / .length.
  const docsArray = Array.isArray(docs) ? docs : [];

  // Mobile-only sort (the list rows have no column headers; the desktop
  // table keeps the server order as before). DD/MM/YYYY → YYYYMMDD so the
  // date sorts chronologically as a string.
  const { sorted: sortedDocs, sort: docSort, setSortTo: setDocSort } = useSortable(docsArray, {
    date:     d => (d.date ? d.date.split('/').reverse().join('') : null),
    total:    d => Math.abs(d.total || 0),
    supplier: d => d.supplier_name,
  });

  // Route to the document-view page (same destination as the supplier-docs
  // page uses). DocumentViewPage owns the multi-page navigation arrows, so
  // 2+-page docs work uniformly. We pass autoOpen via location state so the
  // file panel expands immediately — the user just clicked "צפה במסמך", they
  // want to see it without an extra toggle.
  // window.open was unreliable here: setOpenMenu(null) before window.open
  // breaks the user-gesture chain in some browsers and the popup gets blocked,
  // leaving only the menu visible (which is what was being reported).
  const openDocument = (d) => {
    // Desktop keeps auto-expanding the file panel (the user clicked "צפה
    // במסמך", they want the scan). On a phone the file viewer would swallow
    // the whole first viewport — land on the document data instead and let
    // the user open the file explicitly.
    navigate(`/document/${d.id}`, { state: { autoOpenFiles: !isMobile } });
  };

  // Download the original files (PDF/images) of every currently filtered
  // document as one ZIP. Same authed-blob mechanism as the Excel export, and
  // scope is enforced server-side — the ZIP contains exactly the tenant-scoped,
  // filtered rows shown in the table.
  const LARGE_SET = 50; // above this, warn the download may take a few seconds
  const [zipBusy, setZipBusy] = useState(false);
  const downloadDocs = async () => {
    const withFile = docsArray.filter(d => d.file_path).length;
    if (withFile === 0) {
      toast('אין קבצים להורדה', 'error');
      return;
    }
    setZipBusy(true);
    // One toast that transitions "preparing…" → "download started" (never two).
    const tid = toast(withFile > LARGE_SET
      ? `מכין את הקבצים… (${withFile} מסמכים, עשוי להימשך מספר שניות)`
      : 'מכין את הקבצים…');
    try {
      await documentsApi.downloadFilesZip(params);
      updateToast(tid, 'ההורדה החלה', 'success');
    } catch (err) {
      updateToast(tid, err.message || 'שגיאה בהורדת הקבצים', 'error');
    } finally {
      setZipBusy(false);
    }
  };

  // The reason dialog (components/DeleteDocumentDialog.jsx) owns the request
  // and its toast; this page reloads once the document is gone.
  const [deleting, setDeleting] = useState(null);
  const handleDelete = (d) => setDeleting(d);

  const menuBtn = { display: 'block', width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '0.88rem', color: '#333', fontFamily: 'inherit' };

  // Footer total: signed sum (credit-type docs subtract).
  const totalAmount = docsArray.reduce(
    (s, d) => s + (isCreditDoc(d.doc_type) ? -Math.abs(d.total || 0) : (d.total || 0)),
    0,
  );

  return (
    <div>
      <div className="page-title-row">
        <h1 className="page-title">מסמכים</h1>
        {/* Both act on everything the filters show, so they sit in the title
            row next to ⋮ like every page-level action (design system,
            "Where buttons go"). Mirrored in the ⋮ menu, as on other pages. */}
        <div className="page-title-actions">
          <button className="btn btn-sm btn-secondary"
            disabled={docsArray.length === 0 || zipBusy}
            onClick={downloadDocs}
            aria-label="הורדה — כל המסמכים המסוננים כקובץ ZIP"
            aria-busy={zipBusy}
            title="הורדת כל המסמכים המסוננים כקובץ ZIP אחד">
            {zipBusy ? (
              // Loading: spinner replaces the icon; button is disabled.
              <span aria-hidden="true" style={{
                width: 14, height: 14, display: 'inline-block', borderRadius: '50%',
                border: '2px solid var(--line)', borderTopColor: 'var(--action)',
                animation: 'spin 0.7s linear infinite',
              }} />
            ) : (
              // Arrow-into-tray (Lucide Download) — the universal "download
              // everything here" glyph; FileDown read as single-file.
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"
                   fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            <span>הורדה</span>
          </button>
          <button className="btn btn-sm btn-excel"
            disabled={docsArray.length === 0}
            onClick={() => documentsApi.exportAll(params)}
            title="ייצוא לאקסל">
            <Icon name="spreadsheet" size={14} />
            ייצוא
          </button>
          <div className="page-menu">
            <button
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel">
                  <button className="page-menu-item"
                    disabled={docsArray.length === 0 || zipBusy}
                    onClick={() => { setPageMenuOpen(false); downloadDocs(); }}>
                    <Icon name="download" size={16} /> הורדת המסמכים
                  </button>
                  <button className="page-menu-item"
                    disabled={docsArray.length === 0}
                    onClick={() => { setPageMenuOpen(false); documentsApi.exportAll(params); }}>
                    <Icon name="spreadsheet" size={16} /> ייצוא לאקסל
                  </button>
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <DocumentsFilterBar
        suppliers={suppliers}
        supplierIds={supplierIds}
        setSupplierIds={setSupplierIds}
        docTypes={docTypes}
        setDocTypes={setDocTypes}
        docTypeOptions={ALL_DOC_TYPES}
        docNumFilter={docNumFilter}
        setDocNumFilter={setDocNumFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        onClearAll={clearFilters}
        resultCount={docsArray.length}
        priceSources={priceSources}
        setPriceSources={isAdmin ? setPriceSources : undefined}
        sortControl={
          <MobileSort
            options={[
              { key: 'date',     label: 'תאריך' },
              { key: 'total',    label: 'סכום' },
              { key: 'supplier', label: 'ספק' },
            ]}
            sort={docSort}
            onChange={setDocSort}
          />
        }
      />

      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את המסמכים" onRetry={load} />
        ) : docsArray.length === 0 ? (
          <EmptyState compact={hasFilters} message={hasFilters ? `לא נמצאו מסמכים ב${periodLabel(dateFrom, dateTo)} לפי הסינון שנבחר` : 'אין מסמכים'} />
        ) : isMobile ? (
          /* Mobile: two-line list rows instead of a sideways-scrolling table.
             Row tap opens the document view (which owns file preview,
             download, and delete — no per-row ⋮ needed here). */
          <div className="m-list">
            {sortedDocs.map(d => {
              const credit = isCreditDoc(d.doc_type);
              // Inside the type chip, so the icon goes wherever the chip goes
              // (beside the name, or down to line 2 at ≤360px). Icon only on a
              // phone — no hover to reveal a tip.
              const linkMark = d.linked_invoice_doc_num != null
                ? <LinkedNoteMark invoiceDocNum={d.linked_invoice_doc_num} />
                : null;
              return (
                <ListRow
                  key={d.id}
                  /* Rev 2 per design+UX review: supplier is the text anchor
                     with the type as a NEUTRAL suffix chip (the status badge
                     stays the row's only colored element); line 2 = status +
                     date; the doc number sits under the amount as a quiet
                     tabular figure — findable for verification, out of the
                     meta string. */
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.supplier_name}
                      </span>
                      <HqOnlyLock hqOnly={d.supplier_hq_only} />
                      <span className="m-type-chip m-type-chip--name">{d.doc_type}{linkMark}</span>
                    </span>
                  }
                  /* The type chip renders twice and CSS shows one: beside the
                     name, or on line 2 on the narrowest phones (≤360px), where
                     beside the name it cut every supplier name to ~95px
                     (owner, 2026-10-05). The hidden one is display:none, so
                     screen readers hear it once. */
                  meta={
                    <>
                      <span className={`badge ${statusBadge(d.status)}`}>{d.status}</span>
                      <span>{formatDateShort(d.date) || '—'}</span>
                      <span className="m-type-chip m-type-chip--meta">{d.doc_type}{linkMark}</span>
                      <DocPriceTags doc={d} isAdmin={isAdmin} phone />
                    </>
                  }
                  amount={credit
                    ? <span style={{ color: '#c0392b' }}>{formatCurrency(-Math.abs(d.total) || 0)}</span>
                    : formatCurrency(d.total)}
                  endSub={d.doc_num
                    ? <span className="m-endsub-num">#{d.doc_num}</span>
                    : null}
                  onClick={() => openDocument(d)}
                />
              );
            })}
            <div className="m-list-foot">
              <span>סה"כ - {docsArray.length} מסמכים</span>
              <span className="m-row-amount">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        ) : (
          <div className="table-wrapper table-scroll-mobile">
            <table>
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>ספק</th>
                  <th>סוג מסמך</th>
                  <th>מספר מסמך</th>
                  <th>סכום</th>
                  <th>סטטוס</th>
                  <th>הועלה</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {docsArray.map(d => {
                  const credit = isCreditDoc(d.doc_type);
                  return (
                    <tr key={d.id}>
                      <td>{formatDateShort(d.date) || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{d.supplier_name}<HqOnlyLock hqOnly={d.supplier_hq_only} /></td>
                      <td>
                        <span className="doc-type-group">
                          <span className={`badge ${credit ? 'badge-red' : 'badge-blue'}`}>
                            {d.doc_type}
                          </span>
                          {/* Every role (owner, 16/10/2026): this note is linked;
                              the tooltip names the מרכזת. The server leaves the
                              field NULL when this role may not see the invoice
                              (a draft, or a בעלים/מטה בלבד supplier). */}
                          {d.linked_invoice_doc_num != null && (
                            <LinkedNoteMark invoiceDocNum={d.linked_invoice_doc_num} showTip />
                          )}
                        </span>
                        <DocPriceTags doc={d} isAdmin={isAdmin} />
                      </td>
                      <td style={{ color: 'var(--ink-muted)' }}>{d.doc_num || '—'}</td>
                      <td style={credit ? { color: '#c0392b', fontWeight: 600 } : { fontWeight: 600 }}>
                        {credit ? formatCurrency(-Math.abs(d.total) || 0) : formatCurrency(d.total)}
                      </td>
                      <td><span className={`badge ${statusBadge(d.status)}`}>{d.status}</span></td>
                      <td style={{ color: 'var(--ink-muted)', whiteSpace: 'nowrap' }} title={formatUploadedFull(d.created_at)}>
                        {formatUploadedShort(d.created_at) || '—'}
                      </td>

                      {/* ⋮ menu — view file (works for any doc_type) + delete */}
                      <td onClick={e => e.stopPropagation()} style={{ position: 'relative', width: 36, textAlign: 'center' }}>
                        <button
                          className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ink-muted)', padding: '2px 4px', lineHeight: 1 }}
                          onClick={() => setOpenMenu(openMenu === d.id ? null : d.id)}
                        >⋮</button>
                        {openMenu === d.id && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 198 }} onClick={() => setOpenMenu(null)} />
                            <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 199, background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 170, overflow: 'hidden' }}>
                              <button className="row-menu-item" style={menuBtn}
                                onClick={() => { setOpenMenu(null); openDocument(d); }}>
                                <Icon name="eye" size={16} /> צפה במסמך
                              </button>
                              {d.file_path && (
                                <button className="row-menu-item" style={menuBtn}
                                  onClick={() => { setOpenMenu(null); downloadDocFile(d.file_path); }}>
                                  <Icon name="download" size={16} /> הורד קובץ
                                </button>
                              )}
                              {isAdmin && (
                                <>
                                  <div style={{ borderTop: '1px solid #f0f0f0' }} />
                                  <button className="row-menu-item" style={{ ...menuBtn, color: '#b91c1c' }}
                                    onClick={() => { setOpenMenu(null); handleDelete(d); }}>
                                    <Icon name="trash" size={16} /> מחק מסמך
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#eaf0f8', fontWeight: 700, borderTop: '2px solid #c5d5e8' }}>
                  <td colSpan={4} style={{ padding: '10px 16px', color: '#1a1a2e' }}>
                    סה"כ - {docsArray.length} מסמכים
                  </td>
                  <td style={{ padding: '10px 16px' }}>{formatCurrency(totalAmount)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {deleting && (
        <DeleteDocumentDialog
          doc={deleting}
          title="מחיקת מסמך"
          confirmLabel="מחק מסמך"
          successMessage="המסמך נמחק"
          context={`${deleting.supplier_name ? `${deleting.supplier_name} · ` : ''}${formatCurrency(deleting.total)}.`}
          onCancel={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
}
